import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

let addPending = false;
let needsCartReview = false;

function updateButtons() {
  for (const component of document.querySelectorAll('lhaptic-cart-upsell')) {
    const button = component.querySelector('button[type="submit"]');
    const selector = component.querySelector('select[name="id"]');
    if (button) {
      button.disabled = addPending || needsCartReview;
      button.textContent = addPending ? component.dataset.addingLabel : component.dataset.addLabel;
    }
    if (selector) selector.disabled = addPending || needsCartReview;
    component.setAttribute('aria-busy', String(addPending));
    if (needsCartReview) {
      const status = component.querySelector('[role="status"]');
      if (status) status.textContent = component.dataset.reviewMessage;
    }
  }
}

class LhapticCartUpsell extends HTMLElement {
  connectedCallback() {
    this.querySelector('form')?.addEventListener('submit', this.onSubmit);
    this.querySelector('select[name="id"]')?.addEventListener('change', this.onVariantChange);
    updateButtons();
  }

  disconnectedCallback() {
    this.querySelector('form')?.removeEventListener('submit', this.onSubmit);
    this.querySelector('select[name="id"]')?.removeEventListener('change', this.onVariantChange);
  }

  onVariantChange = () => {
    const option = this.querySelector('select[name="id"]')?.selectedOptions[0];
    const price = this.querySelector('.cart-upsell__price bdi');
    const image = this.querySelector('.cart-upsell__image');
    if (option?.dataset.price && price) price.textContent = option.dataset.price;
    if (option?.dataset.image && image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }
    const reason = this.querySelector('.cart-upsell__reason');
    if (option && reason) {
      reason.hidden = this.dataset.qualificationEnabled !== 'true'
        || Number(option.dataset.priceCents) < Number(this.dataset.remainingAmount);
      reason.textContent = this.dataset.qualifyingTemplate.replace('__PRICE__', option.dataset.price || '');
    }
  };

  onSubmit = async (event) => {
    event.preventDefault();
    if (addPending || needsCartReview) return;
    const variantId = this.querySelector('[name="id"]')?.value;
    if (!variantId || !Number.isSafeInteger(Number(variantId))) return;

    addPending = true;
    updateButtons();
    const status = this.querySelector('[role="status"]');
    if (status) status.textContent = '';
    let deferred;
    let requestSent = false;
    let rejectionConfirmed = false;
    let addConfirmed = false;

    try {
      const pendingUpdates = customElements.get('cart-items-component')?.pendingQuantityUpdates || [];
      await Promise.all(pendingUpdates);
      const sections = [...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((element) => element.dataset.sectionId)
          .filter(Boolean)
      )].slice(0, 5);

      deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      }));

      requestSent = true;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
        signal: AbortSignal.timeout(15000),
      });
      rejectionConfirmed = response.status >= 400 && response.status < 500;
      const result = await response.json();
      if (!response.ok || result.status) {
        rejectionConfirmed = response.ok || rejectionConfirmed;
        throw new Error(typeof result.description === 'string' ? result.description : this.dataset.errorMessage);
      }
      addConfirmed = true;

      const cartResponse = await fetch(Theme.routes.cart_url + '.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal: AbortSignal.timeout(15000),
      });
      if (!cartResponse.ok) throw new Error(this.dataset.reviewMessage);
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'lhaptic-cart-upsell',
          itemCount: cart.item_count,
          sections: result.sections,
          didError: false,
        },
      });
      if (status) status.textContent = this.dataset.addedLabel;
    } catch (error) {
      needsCartReview = requestSent && (addConfirmed || !rejectionConfirmed);
      const message = needsCartReview
        ? this.dataset.reviewMessage
        : (error?.message || this.dataset.errorMessage);
      if (status) status.textContent = message;
      deferred?.reject(error);
      document.dispatchEvent(new CartErrorEvent({
        error: message,
        code: rejectionConfirmed ? 'INVALID' : 'SERVICE_UNAVAILABLE',
      }));
    } finally {
      addPending = false;
      updateButtons();
    }
  };
}

if (!customElements.get('lhaptic-cart-upsell')) {
  customElements.define('lhaptic-cart-upsell', LhapticCartUpsell);
}
